/**
 * deepspace login
 *
 * Opens the browser to sign in with GitHub or Google.
 * CLI polls the auth worker until login is complete.
 *
 *   deepspace login                          # browser-based OAuth
 *   deepspace login --email x --password y   # non-interactive (CI/agents)
 */

import { defineCommand } from 'citty'
import { writeFileSync, mkdirSync } from 'node:fs'
import { join } from 'node:path'
import { homedir } from 'node:os'
import { randomBytes, createHash } from 'node:crypto'
import * as p from '@clack/prompts'

import { createSpinner } from '../lib/spinner'
import { DASHBOARD_URL, PLATFORM_URLS } from '../env'
import { exchangeSession, SESSION_COOKIE } from '../session'
import { openBrowser } from '../lib/open-browser'

const AUTH_URL = process.env.DEEPSPACE_AUTH_URL ?? PLATFORM_URLS.auth

export default defineCommand({
  meta: {
    name: 'login',
    description: 'Log in to your DeepSpace account',
  },
  args: {
    email: {
      type: 'string',
      description: 'Email address (non-interactive mode)',
      required: false,
    },
    password: {
      type: 'string',
      description: 'Password (non-interactive mode)',
      required: false,
    },
  },
  async run({ args }) {
    // Non-interactive mode for CI/agents with email+password
    if (args.email && args.password) {
      console.log(`Signing in as ${args.email}...`)
      await doEmailLogin(args.email, args.password)
      console.log('Logged in')
      return
    }

    // Browser-based OAuth flow
    p.intro('DeepSpace Login')

    const s = createSpinner()
    s.start('Creating login session...')

    // PKCE: generate a random verifier and send only its SHA-256 hash to the
    // server. The verifier never leaves this process, so the loginUrl alone
    // can't be used to drain credentials at /status — only this CLI can.
    const codeVerifier = base64url(randomBytes(32))
    const codeChallenge = base64url(createHash('sha256').update(codeVerifier).digest())

    // 1. Create a CLI session
    const sessionRes = await fetch(`${AUTH_URL}/api/auth/cli/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code_challenge: codeChallenge }),
    })
    if (!sessionRes.ok) {
      s.stop('Failed')
      p.cancel('Could not create login session')
      process.exit(1)
    }
    const sessionData = (await sessionRes.json()) as {
      sessionId?: string
      loginUrl?: string
    }
    if (!sessionData.sessionId || !sessionData.loginUrl) {
      s.stop('Failed')
      p.cancel('Could not create login session')
      process.exit(1)
    }

    const { sessionId, loginUrl } = sessionData as {
      sessionId: string
      loginUrl: string
    }

    s.stop('Opening browser...')
    p.log.info(`If the browser doesn't open, visit:\n  ${loginUrl}`)

    // 2. Open browser
    openBrowser(loginUrl)

    // 3. Poll for completion
    s.start('Waiting for authentication...')
    const result = await pollForCompletion(sessionId, codeVerifier)

    if (!result) {
      s.stop('Timed out')
      p.cancel('Login timed out. Run `deepspace login` to try again.')
      process.exit(1)
    }

    // 4. Store credentials
    storeCredentials(result.sessionToken, result.jwt)

    s.stop('Authenticated')
    p.log.success(`Logged in as ${result.name ?? result.email}`)
    p.note(
      `Manage your account, deployed apps, billing, and subscription\nplans on the web dashboard:\n\n  ${DASHBOARD_URL}`,
      'Dashboard',
    )
    p.outro('Done')
  },
})

interface LoginResult {
  sessionToken: string
  jwt: string
  email: string
  name: string | null
}

async function pollForCompletion(
  sessionId: string,
  codeVerifier: string,
): Promise<LoginResult | null> {
  const maxAttempts = 120 // 10 minutes at 5s intervals
  const interval = 5000

  for (let i = 0; i < maxAttempts; i++) {
    await sleep(interval)

    try {
      const res = await fetch(`${AUTH_URL}/api/auth/cli/status/${sessionId}`, {
        headers: { 'x-code-verifier': codeVerifier },
      })

      if (res.status === 410 || res.status === 404) return null // expired / not found
      if (!res.ok) continue

      const data = (await res.json()) as {
        state?: string
        sessionToken?: string
        jwt?: string
        email?: string
        name?: string
      }

      if (data.state === 'complete' && data.sessionToken && data.jwt) {
        return {
          sessionToken: data.sessionToken,
          jwt: data.jwt,
          email: data.email ?? '',
          name: data.name ?? null,
        }
      }
    } catch {
      // Network error, keep polling
    }
  }

  return null
}

function storeCredentials(sessionToken: string, jwt: string) {
  const dir = join(homedir(), '.deepspace')
  mkdirSync(dir, { recursive: true, mode: 0o700 })
  writeFileSync(join(dir, 'session'), sessionToken, { mode: 0o600 })
  writeFileSync(join(dir, 'token'), jwt, { mode: 0o600 })
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function base64url(bytes: Buffer): string {
  return bytes.toString('base64').replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

// ── Email/password login (for test accounts and CI) ────────────────

async function doEmailLogin(email: string, password: string): Promise<void> {
  const res = await fetch(`${AUTH_URL}/api/auth/sign-in/email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Origin: AUTH_URL },
    body: JSON.stringify({ email, password }),
    redirect: 'manual',
  })

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string }
    throw new Error(body.message ?? `Authentication failed (${res.status})`)
  }

  const setCookie = res.headers.get('set-cookie') ?? ''
  const cookieMatch = setCookie.match(new RegExp(`${SESSION_COOKIE}=([^;]+)`))
  if (!cookieMatch) {
    throw new Error('No session cookie returned')
  }
  const sessionToken = decodeURIComponent(cookieMatch[1])

  const jwt = await exchangeSession(AUTH_URL, sessionToken)
  if (!jwt) {
    throw new Error('JWT issuance failed')
  }

  storeCredentials(sessionToken, jwt)
}
